import React, { useState, useEffect } from 'react';
import Link from '@mui/material/Link';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Card,
  CardHeader,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  Checkbox,
  Tooltip,
  TextField,
  InputAdornment,
  Avatar,
  Paper,
} from '@mui/material';
import { IconButton, Collapse } from '@mui/material';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
// import SendIcon from '@mui/icons-material/Send';

import Button from '@mui/material/Button';
import Title from '../../components/Title';
import SearchBar from '../../components/SearchBar';
import EditModal from '../../components/EditModal';
import AddModal from '../../components/AddModal';
import AssignMultipleModal from '../../components/AssignMultipleModal';
import UploadModal from '../../components/UploadModal';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import { ProfileAvatar } from '../../components/DynamicForm';
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, NA_LABEL, SEND_INVITE_URL } from '../../common/AppConstants';

const TITLE = 'Teachers';
const API_URL = 'academics/teacher/';
const SUBJECT_API_URL = 'academics/teacher-subject/'; // Constant for the Teacher Subject API endpoint
const SUBJECT_CLASS_LEVEL_API_URL = 'academics/staffsubject-classlevel/'; // API for class level assignments within subjects
const SUBJECT_CLASS_LEVEL_BULK_API_URL = 'academics/staffsubject-classlevel/bulk/';
const SUBJECT_SECTION_BULK_API_URL = 'academics/staffsubject-section/bulk/';
const UPLOAD_TEMPLATE = 'teacher_upload_template.xlsx';
const INVITE_TYPE = 'teacher';
const NO_DATA_AVAILABLE_STRING =
  'No data available, please start adding now !!!';

const subjectAssignColumns = [
  {
    attr: 'subject',
    editable: true,
    extra: false,
    label: 'Subject',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Subject',
  },
];

const classLevelAssignColumn = {
  attr: 'class_level',
  editable: true,
  extra: false,
  label: 'Class Levels',
  related: true,
  required: true,
  type: 'multiple_choice',
  help_text: 'Class Levels',
};

const sectionAssignColumn = {
  attr: 'section',
  editable: true,
  extra: false,
  label: 'Sections',
  related: true,
  required: true,
  type: 'multiple_choice',
  help_text: 'Sections',
};

const sendInviteColumns = [
  {
    attr: 'email',
    editable: false,
    extra: false,
    label: 'Email',
    related: false,
    required: true,
    type: 'text',
    help_text: 'Send invite at email',
  },
];

function preventDefault(event) {
  event.preventDefault();
}

export default function Teachers() {
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [relatedData, setRelatedData] = useState({});
  const [selectedEditRow, setSelectedEditRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtered data based on search
  const filteredRows = rows.filter(row_item =>
    row_item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    row_item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    row_item.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (`${row_item.first_name} ${row_item.last_name}`).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddClick = () => {
    setIsAddModalOpen(true);
  };

  const handleAddModalClose = () => {
    setIsAddModalOpen(false);
  };

  const handleUploadClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleEditClick = (row) => {
    console.log('Edit row ', row);
    setSelectedEditRow(row);
  };

  useEffect(() => {
    if (selectedEditRow !== null && selectedEditRow !== undefined) {
      // Only open the modal after the row has been selected
      setIsEditModalOpen(true);
    }
  }, [selectedEditRow]); // Runs when selectedEditRow changes

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedEditRow(null);
  };

  const handleEditSave = async (updatedEditRow, onEditFailure) => {
    await httpRequest({
      method: 'PUT',
      url: API_URL,
      data: updatedEditRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.map((row, index) =>
            row.id === selectedEditRow.id ? { ...row, ...data } : row,
          ),
        );
        handleEditModalClose();
      },
      onFailure: (data) => {
        onEditFailure(data);
      },
      addNotification,
    });
  };

  const handleDelete = async (deletedRow) => {
    await httpRequest({
      method: 'DELETE',
      url: API_URL,
      data: deletedRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.filter((row) => row.id !== deletedRow.id),
        );
      },
      addNotification,
    });
  };

  const handleAddSave = async (addFormData, onAddSuccess, onAddFailure) => {
    await httpRequest({
      method: 'POST',
      url: API_URL,
      data: addFormData,
      onSuccess: (data) => {
        handleAddModalClose();
        setRows((prevRows) => [data.entry, ...prevRows]);
        handleAddClick();
        onAddSuccess();
      },
      onFailure: (data) => {
        onAddFailure(data);
      },
      addNotification,
    });
  };

  const handleUpload = async (file, onUploadSuccess, onUploadFailure) => {
    const formData = new FormData();
    formData.append('file', file);

    await httpRequest({
      method: 'POST',
      url: `${API_URL}upload/`,
      data: formData,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
      onSuccess: (data) => {
        refreshData();
        onUploadSuccess(data);
      },
      onFailure: (data) => {
        onUploadFailure(data);
      },
      addNotification,
    });
  };

  const handleFilterApply = async () => {
    // ToDo: Filter table data
  };

  useEffect(() => {
    const fetchData = async () => {
      refreshData();
    };
    fetchData();
  }, []);

  const refreshData = async (event) => {
    setIsLoading(true);

    await httpRequest({
      method: 'GET',
      url: API_URL,
      onSuccess: (data) => {
        setColumns(data.columns);
        setRows(data.rows);
        setRelatedData(data.relatedData);
      },
      onFailure: (data) => {
        setColumns([]);
        setRows([]);
        setRelatedData({});
      },
      addNotification,
    });
    
    setIsLoading(false);
  };

  const TeacherSubjectCard = ({ teacher_row, teacher_dict, relatedData }) => {
    // const incomplete_data = teacher_dict['subject_list'].length === 0;  // && !teacher_dict.can_teach_all_subjects
    const [expanded, setExpanded] = useState(false);  // incomplete_data
    const [selectedRemoveSubject, setSelectedRemoveSubject] = useState(null);
    const [selectedRemoveClassLevel, setSelectedRemoveClassLevel] = useState(null);
    const [isSubjectAddModalOpen, setIsSubjectAddModalOpen] = useState(false);
    const [selectedSubjectForClassLevel, setSelectedSubjectForClassLevel] = useState(null);
    const [selectedSubjectForSection, setSelectedSubjectForSection] = useState(null);
    const [isClassLevelAddModalOpen, setIsClassLevelAddModalOpen] = useState(false);
    const [isSectionAddModalOpen, setIsSectionAddModalOpen] = useState(false);
    const [isSendInviteModalOpen, setIsSendInviteModalOpen] = useState(false);

    const handleExpandClick = () => {
      setExpanded((prev) => !prev);
    };

    const handleAssignModalClose = () => {
      setIsSubjectAddModalOpen(false);
    };

    const handleClassLevelAssignModalClose = () => {
      setIsClassLevelAddModalOpen(false);
      setSelectedSubjectForClassLevel(null);
    };

    const handleSectionAssignModalClose = () => {
      setIsSectionAddModalOpen(false);
      setSelectedSubjectForSection(null);
    };

    const handleSendInviteModalOpen = () => {
      setIsSendInviteModalOpen(true);
    };

    const handleSendInviteModalClose = () => {
      setIsSendInviteModalOpen(false);
    };

    const handleAssignSave = async (addFormData, onAddSuccess, onAddFailure) => {
      const finalFormData = {
        ...addFormData,
        staff: teacher_dict.id,
      };
      
      await httpRequest({
        method: 'POST',
        url: SUBJECT_API_URL,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === teacher_dict.id
                ? {
                    ...row,
                    subject_list: [
                      ...row.subject_list,
                      data.entry
                    ],
                  }
                : row,
            ),
          );
          // setIsSubjectAddModalOpen(false);
          onAddSuccess();
        },
        onFailure: (data) => {
          onAddFailure(data);
        },
        addNotification,
      });
    };

    const handleClassLevelAssignSave = async (addFormData, onAddSuccess, onAddFailure) => {
      const finalFormData = {
        class_level_list: addFormData,
        staff_subject: selectedSubjectForClassLevel.id,
      };
      
      await httpRequest({
        method: 'PUT',
        url: SUBJECT_CLASS_LEVEL_BULK_API_URL,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === teacher_dict.id
                ? {
                    ...row,
                    subject_list: row.subject_list.map((subject) =>
                      subject.id === selectedSubjectForClassLevel.id
                        ? {
                            ...subject,
                            class_level_list: data.class_level_list,
                          }
                        : subject
                    ),
                  }
                : row,
            ),
          );
          onAddSuccess();
        },
        onFailure: (data) => {
          onAddFailure(data);
        },
        addNotification,
      });
    };

    const handleSectionAssignSave = async (addFormData, onAddSuccess, onAddFailure) => {
      const finalFormData = {
        section_list: addFormData,
        staff_subject: selectedSubjectForSection.id,
      };
      
      await httpRequest({
        method: 'PUT',
        url: SUBJECT_SECTION_BULK_API_URL,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === teacher_dict.id
                ? {
                    ...row,
                    subject_list: row.subject_list.map((subject) =>
                      subject.id === selectedSubjectForSection.id
                        ? {
                            ...subject,
                            section_list: data.section_list,
                          }
                        : subject
                    ),
                  }
                : row,
            ),
          );
          onAddSuccess();
        },
        onFailure: (data) => {
          onAddFailure(data);
        },
        addNotification,
      });
    };

    const handleSendInvite = async (row_dict, onEditFailure) => {
      if (row_dict.verified) {
        addNotification("Already joined", 'info');
        return;
      }

      if (!row_dict.email) {
        addNotification("Please add email address to send invitation", 'warning');
        return;
      }

      const data = {
        invite_type: INVITE_TYPE,
        profile_id: row_dict.id,
      };
      await httpRequest({
        method: 'POST',
        url: SEND_INVITE_URL,
        data: data,
        onSuccess: (data) => {
          handleSendInviteModalClose();
        },
        addNotification,
      });
    };

    const closeDeleteConfirmation = () => {
      setSelectedRemoveSubject(null);
    };

    const closeClassLevelDeleteConfirmation = () => {
      setSelectedRemoveClassLevel(null);
    };

    const handleRemoveSubject = async () => {
      await httpRequest({
        method: 'DELETE',
        url: SUBJECT_API_URL,
        data: {
          id: selectedRemoveSubject.id,
        },
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === teacher_dict.id
                ? {
                    ...row,
                    subject_list: row.subject_list.filter(
                      (subject_comp) => subject_comp.id !== selectedRemoveSubject.id,
                    ),
                  }
                : row,
            ),
          );
          setSelectedRemoveSubject(null);
        },
        addNotification,
      });
    };

    const handleRemoveClassLevel = async () => {
      await httpRequest({
        method: 'DELETE',
        url: SUBJECT_CLASS_LEVEL_API_URL,
        data: {
          id: selectedRemoveClassLevel.id,
        },
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === teacher_dict.id
                ? {
                    ...row,
                    subject_list: row.subject_list.map((subject) =>
                      subject.id === selectedRemoveClassLevel.subject_id
                        ? {
                            ...subject,
                            class_level_list: subject.class_level_list.filter(
                              (classLevel) => classLevel.id !== selectedRemoveClassLevel.id,
                            ),
                          }
                        : subject
                    ),
                  }
                : row,
            ),
          );
          setSelectedRemoveClassLevel(null);
        },
        addNotification,
      });
    };

    const SubjectWithClassLevelsCard = ({ subject_dict }) => (
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          transition: 'box-shadow 0.3s',
          '&:hover': {
            boxShadow: 3
          }
        }
      }>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4">{relatedData?.['subject']?.[subject_dict.subject]?.['label'] || 'Unknown Subject'}</Typography>
            </Box>
          }
          action={
            <IconButton
              onClick={() =>
                setSelectedRemoveSubject({
                  subject: subject_dict.subject,
                  id: subject_dict.id,
                })
              }
              size="small"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          }
          sx={{ py: 1 }}
        />
        <Divider/>
        
        <CardContent sx={{ py: 2, flexGrow: 1, display: 'flex' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Assign Section Chip */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddCircleIcon />}
                onClick={() => {
                  setSelectedSubjectForSection(subject_dict);
                  setIsSectionAddModalOpen(true);
                }}
                // sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
              >
                Assign Section
              </Button>
              {subject_dict.section_list && subject_dict.section_list.length > 0 && (
                subject_dict.section_list.map((section) => (
                  <Chip
                    key={section.id}
                    label={relatedData?.['section']?.[section.section]?.['label'] || NA_LABEL}
                    color="primary"
                    variant="outlined"
                    // onDelete={() =>
                    //   setSelectedRemoveSection({
                    //     section: section.section,
                    //     id: section.id,
                    //     subject_id: subject_dict.id,
                    //   })
                    // }
                    // deleteIcon={<CancelIcon />}
                  />
                ))
              )}
            </Box>
            
            {/* Assign Class Level Chip */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddCircleIcon />}
                onClick={() => {
                  setSelectedSubjectForClassLevel(subject_dict);
                  setIsClassLevelAddModalOpen(true);
                }}
                // sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
              >
                Assign Class
              </Button>
              {subject_dict.class_level_list && subject_dict.class_level_list.length > 0 ? (
                subject_dict.class_level_list.map((classLevel) => (
                  <Chip
                    key={classLevel.id}
                    label={relatedData?.['class_level']?.[classLevel.class_level]?.['label'] || NA_LABEL}
                    color="primary"
                    variant="outlined"
                    onDelete={() =>
                      setSelectedRemoveClassLevel({
                        class_level: classLevel.class_level,
                        id: classLevel.id,
                        subject_id: subject_dict.id,
                      })
                    }
                    deleteIcon={<CancelIcon />}
                  />
                ))
              ) : (
                <Chip
                  // key={classLevel.id}
                  label='All Class Levels'
                  color="secondary"
                  variant="outlined"
                />
              )}
              {/* <Box component="span" sx={{ mr: 1 }}>
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AddCircleIcon sx={{ mr: 0.5, fontSize: '1.5rem' }} />
                      {' '}Assign Class
                    </Box>
                  }
                  color="default"
                  variant="outlined"
                  onClick={() => {
                    setSelectedSubjectForClassLevel(subject_dict);
                    setIsClassLevelAddModalOpen(true);
                  }}
                  sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    // px: 1.5,
                    // mt: 1,
                    py: 0.5,
                    width: 'fit-content', // prevents full-width if outer container allows expansion
                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                  }}
                />
              </Box> */}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );

    return (
      <Box>
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: 3
            }
          }
        }>
          <CardHeader
            avatar={
              <ProfileAvatar dataDict={teacher_dict} handleSendInviteModalOpen={handleSendInviteModalOpen} />
            }
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5">{`${teacher_dict['code']} - ${teacher_dict['first_name']} ${teacher_dict['last_name']}`}</Typography>
              </Box>
            }
            action={
              <IconButton onClick={() => handleEditClick(teacher_dict)} size="small">
                <EditTwoToneIcon fontSize="small" />
              </IconButton>
            }
            sx={{
              px: 2,
              py: 0,
              alignItems: 'center',
              '& .MuiCardHeader-action': {
                alignSelf: 'center',
              },
            }}
            slotProps={{
              content: { sx: { py: 1.5 } },
            }}
          />
          <Divider/>
          
          <CardContent sx={{ pt: 0, pb: 1, flexGrow: 1, display: 'flex' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1, pt: 2, pb: 2  }}>
              {/* <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Department:</Typography> {relatedData['department'][teacher_dict['department']]['label'] ?? NA_LABEL}
              </Typography> */}
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Email:</Typography> {teacher_dict['email'] ?? NA_LABEL}
              </Typography>
            </Box>

            {/* Expand Icon */}
            <CardActions disableSpacing sx={{ p: 0}}>
              <IconButton
                size="small"
                onClick={handleExpandClick}
                sx={{ alignSelf: 'flex-start', mt: 1}}
              >
                <ExpandCircleDownOutlinedIcon
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              </IconButton>
            </CardActions>
          </CardContent>
        
          {/* Collapsible Content */}
          <Collapse in={expanded}>
            <Divider sx={{ borderStyle: 'dashed' }} />
            <CardContent sx={{ bgcolor: '#FBFBFB' }}>
              <Stack spacing={1}>
                {/* Subjects Assignment Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    <Typography component="span" variant="body2" fontWeight="bold">Phone:</Typography> {teacher_dict['phone'] ?? NA_LABEL}
                  </Typography>
                </Box>

              </Stack>
            </CardContent>
          </Collapse>
        </Card>

        {/* Assign Subject Modal */}
        <Box>
          <AddModal
            open={isSubjectAddModalOpen}
            columns={subjectAssignColumns}
            relatedData={relatedData}
            rows={teacher_dict.subject_list}
            handleClose={handleAssignModalClose}
            handleSave={handleAssignSave}
            add_title={`${teacher_dict.code} : Assign Subject`}
          />
        </Box>

        {/* Assign Class Level Modal */}
        <Box>
          <AssignMultipleModal
            open={isClassLevelAddModalOpen}
            column={classLevelAssignColumn}
            relatedData={relatedData}
            rows={selectedSubjectForClassLevel?.class_level_list || []}
            handleClose={handleClassLevelAssignModalClose}
            handleSave={handleClassLevelAssignSave}
            add_title={`${teacher_dict.code} : Assign Subject to Class Levels`}
          />
        </Box>

        {/* Assign Section Modal */}
        <Box>
          <AssignMultipleModal
            open={isSectionAddModalOpen}
            column={sectionAssignColumn}
            relatedData={relatedData}
            rows={selectedSubjectForSection?.section_list || []}
            handleClose={handleSectionAssignModalClose}
            handleSave={handleSectionAssignSave}
            add_title={`${teacher_dict.code} : Assign Subject to Sections`}
          />
        </Box>

        {/* Send Invite Modal */}
        <EditModal
          open={isSendInviteModalOpen}
          columns={sendInviteColumns}
          rowData={teacher_dict}
          relatedData={relatedData}
          handleClose={handleSendInviteModalClose}
          handleSave={handleSendInvite}
          handleDelete={null} // {handleDelete}
          edit_title={`Invite: ${teacher_dict.first_name} ${teacher_dict.last_name} (${teacher_dict.code})`}
        />

        {/* Remove Subject Assignment */}
        <DeleteConfirmationDialog
          open={selectedRemoveSubject !== null}
          onClose={closeDeleteConfirmation}
          onConfirm={handleRemoveSubject}
          action='Remove'
          text={
            selectedRemoveSubject
              ? `Are you sure you want to remove '${relatedData.subject?.[selectedRemoveSubject.subject].label}' from '${teacher_dict.code}'?`
              : 'Are you sure you want to remove this item?'
          }
        />

        {/* Remove Class Level Assignment */}
        <DeleteConfirmationDialog
          open={selectedRemoveClassLevel !== null}
          onClose={closeClassLevelDeleteConfirmation}
          onConfirm={handleRemoveClassLevel}
          action='Remove'
          text={
            selectedRemoveClassLevel
              ? `Are you sure you want to remove '${relatedData?.subject?.[selectedRemoveClassLevel.subject_id]?.label || 'this subject'}' - '${relatedData?.class_level?.[selectedRemoveClassLevel.class_level]?.label || 'this class'}' assignment from '${teacher_dict.code}'?`
              : 'Are you sure you want to remove this assignment?'
          }
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Title>{TITLE}</Title>
          </Box>
          {/* Search Bar */}
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {/* <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUploadClick}
              sx={{
                px: 4,
                display: { md: 'flex', xs: 'none' },
              }}
            >
              Upload
            </Button> */}
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={handleAddClick}
              sx={{
                px: 4,
              }}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Scrollable table container */}
      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          // mt: 2,
          minHeight: 200, // Ensures proper space for loader
          display: 'flex',
          flexDirection: 'column', // Aligns table to the top when shown
          alignItems: isLoading ? 'center' : 'stretch', // Center loader, but stretch table
          justifyContent: isLoading ? 'center' : 'flex-start', // Center loader, start table at top,
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            {rows.length === 0 ? (
              // Display message when there are no rows
              <Box
                sx={{
                  textAlign: 'left',
                }}
              >
                <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  py: 2,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, 1fr)',
                    xl: 'repeat(3, 1fr)',
                  },
                  gap: 1.5, // This creates even spacing without edge padding
                }}
              >
                {filteredRows.map((item_dict, index) => (
                  <Box key={index}>
                    <TeacherSubjectCard
                      teacher_row={index}
                      teacher_dict={item_dict}
                      relatedData={relatedData}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Fade>
        )}
      </Box>

      {/* Add Teacher Modal */}
      <Box>
        <AddModal
          open={isAddModalOpen}
          columns={columns}
          relatedData={relatedData}
          rows={rows}
          handleClose={handleAddModalClose}
          handleSave={handleAddSave}
          add_title="Add Teacher"
        />
      </Box>

      {/* Upload Modal */}
      <UploadModal
        open={isUploadModalOpen}
        uploadTemplate={UPLOAD_TEMPLATE}
        handleClose={handleUploadModalClose}
        handleUpload={handleUpload}
      />

      {/* Edit Teacher Modal */}
      <Box>
        <EditModal
          open={isEditModalOpen}
          columns={columns}
          rowData={selectedEditRow}
          relatedData={relatedData}
          handleClose={handleEditModalClose}
          handleSave={handleEditSave}
          handleDelete={handleDelete}
          edit_title="Edit Teacher"
        />
      </Box>
    </Box>
  );
}
